/**
 * ClrzNotifications is a custom HTML element that manages notification.
 * It handles opening, closing, and marking notifications as read.
 */
import {DeferWebComponent} from '@script/colorz/DeferWebComponent.js';

export default class ClrzNotifications extends DeferWebComponent {
	constructor() {
		super();
		this.btnsOpen = [
			...document.querySelectorAll('[data-clrz-notifications-open]'),
		];
		this.btnsClose = [
			...document.querySelectorAll('[data-clrz-notifications-close]'),
		];
		this.popinContainer = document.querySelector(
			'[data-clrz-notifications-popin-container]',
		);
		this.cards = [
			...document.querySelectorAll('[data-clrz-notifications-card]'),
		];
		this.popins = [
			...document.querySelectorAll('[data-clrz-notifications-popin]'),
		];

		this.notificationBadge = document.querySelector(
			'[data-clrz-notifications-badge]',
		);
		this.elCardRead = [
			...document.querySelectorAll('[data-clrz-notifications-card-not-read]'),
		];
		this.elALinks = [...document.querySelectorAll('a[data-clrz-link-card]')];

		// datas
		this.lastScrollTop = 0;
		this.wasOpen = false; // Flag to track if the notification was open before scrolling down
		this.cardRead = [];

		this.isMobile = window.matchMedia('(max-width: 767px)').matches;
	}

	ready() {
		// Show the notification badge if there are cards
		if (this.cards.length > 0)
			this.notificationBadge.classList.remove('u-opacity-0');

		this.btnsOpen.forEach((btn) => {
			btn.addEventListener('click', this.toggleNotifications.bind(this));
		});

		this.btnsClose.forEach((btn) => {
			btn.addEventListener('click', this.toggleNotifications.bind(this));
		});

		this.elALinks.forEach((link) => {
			link.addEventListener('click', (e) => {
				this.manageLink(e, link);
			});
		});

		this.isMobile &&
			document.addEventListener('click', (e) => {
				this.manageCloseMobile(e);
			});

		this.cardRead = localStorage.getItem('card-read')?.split(',') || [];
		this.markAsRead();

		window.addEventListener('scroll', this.handleScroll.bind(this));
	}

	/**
	 * Toggles the notification panel open or closed.
	 */
	toggleNotifications(forceClose = undefined) {
		this.classList.toggle('is-open', forceClose === false ? false : undefined);

		this.style.height = 'auto';

		this.headerHeight = document
			.querySelector('header')
			.getBoundingClientRect().bottom;

		this.notificationTop = this.classList.contains('is-open')
			? Math.max(0, this.headerHeight)
			: -this.getBoundingClientRect().bottom;

		this.style.top = `${this.notificationTop}px`;

		// Calculate the position of the notification panel and adjust overflow if necessary
		this.notifPosY = this.headerHeight + this.getBoundingClientRect().height;
		if (this.notifPosY >= window.innerHeight) {
			this.style.overflow = 'scroll';
			this.style.bottom = '10px';
		} else if (this.style.overflowY === 'scroll') {
			this.style.overflowY = 'hidden';
			this.style.bottom = 'auto';
		}

		this.wasOpen = this.classList.contains('is-open'); // Update the flag when toggling

		// Mark notifications as read if the panel is opened
		this.cards.forEach((card, index) => {
			const cardHandle = card.getAttribute('data-clrz-notifications-card');
			const cardLink = card.getAttribute('data-clrz-notifications-link');

			if (!cardLink) {
				if (!this.cardRead.includes(cardHandle)) this.cardRead.push(cardHandle);
			}
		});
		localStorage.setItem('card-read', this.cardRead);

		setTimeout(() => {
			this.markAsRead();
		}, 2500);
	}

	/**
	 * Handles the scroll event to close the notification panel when scrolling down.
	 */
	handleScroll() {
		const currentScrollTop =
			window.pageYOffset || document.documentElement.scrollTop;

		if (currentScrollTop > this.lastScrollTop) {
			// downscroll code
			if (this.classList.contains('is-open')) {
				this.classList.remove('is-open');
				this.style.height = '0px';
			}
		} else if (currentScrollTop < this.lastScrollTop) {
			// upscroll code
			this.style.height = 'auto';

			if (this.wasOpen) {
				this.headerHeight = document
					.querySelector('header')
					.getBoundingClientRect().bottom;

				this.notificationTop = Math.max(0, this.headerHeight);
				this.style.top = `${this.notificationTop}px`;

				this.classList.add('is-open');
			}
		} // else was horizontal scroll

		this.lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop; // For Mobile or negative scrolling
	}

	/**
	 * Disables scrolling on the document body.
	 */
	disableScroll() {
		document.body.style.overflow = 'hidden';
	}

	/**
	 * Enables scrolling on the document body.
	 */
	enableScroll() {
		document.body.style.overflow = '';
	}

	/**
	 * Marks a notification as read and updates the local storage.
	 */
	markAsRead() {
		this.cardRead.forEach((card) => {
			this.querySelector(
				`[data-clrz-notifications-card-not-read="${card}"]`,
			)?.setAttribute('hidden', true);
		});

		this.checkAllNotificationsRead();
	}

	/**
	 * Checks if all notifications are read and removes the notification badge if true.
	 */
	checkAllNotificationsRead() {
		const allRead = this.elCardRead.every((card) => {
			const indexCard = card.getAttribute('hidden') === 'true';
			return indexCard;
		});

		if (allRead) this.notificationBadge.setAttribute('hidden', true);
	}

	manageLink(e, link) {
		e.preventDefault();
		const cardHandle = link.getAttribute('data-clrz-link-card');
		if (!this.cardRead.includes(cardHandle)) {
			this.cardRead.push(cardHandle);
			localStorage.setItem('card-read', this.cardRead);
		}
		window.location.href = link.getAttribute('href');
	}

	/**
	 * Handles the closing of the notifications panel on mobile devices.
	 * Determines if the click event occurred outside the notifications component,
	 * and closes the panel if it is open and the click was not on the bell icon.
	 *
	 * @param {Event} e - The click event object.
	 */
	manageCloseMobile(e) {
		this.clickOutside = e.target.closest('clrz-notifications') == null;
		this.clickonBell =
			e.target.closest('[data-clrz-notifications-open]') !== null;
		this.isOpen = this.classList.contains('is-open');
		if (this.clickOutside && this.isOpen && !this.clickonBell) {
			this.toggleNotifications(false);
		}
	}
}

customElements.define('clrz-notifications', ClrzNotifications);
